import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * Styled native `<select>` primitive — shadcn/ui convention on the existing
 * token stack. Uses the native `<select>` element (no new Radix dependency)
 * to stay consistent with the rest of the codebase and avoid bundle overhead.
 *
 * Visual language matches {@link Input}: same border, radius, and focus ring.
 * `bg-crucible-bg` fills the surface so the browser's option list renders
 * against the dark background rather than the OS default white.
 *
 * `app-region-no-drag` is required on every interactive element so the
 * Electron window-drag region does not eat clicks (#531 / #804).
 *
 * For richer popover-style dropdowns (search, multi-select, grouped options),
 * use the existing Radix `DropdownMenu` or add a Radix `react-select` wrapper
 * when needed — the pattern for that is tracked in the design-system backlog
 * (docs/design/design-system.md §3).
 */
export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <select
        ref={ref}
        className={cn(
          'app-region-no-drag w-full rounded-[var(--radius-md)] border border-crucible-border',
          'bg-crucible-bg px-3 py-2 text-sm text-crucible-fg',
          'transition-colors focus:border-crucible-accent focus:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-40',
          className,
        )}
        {...props}
      >
        {children}
      </select>
    );
  },
);
Select.displayName = 'Select';
